import SelectorEngine from '../../utils/dom/selector-engine';
import { takeWalletPayment } from './clubspark-api';
import EventHandler from '../../utils/dom/event-handler';
import { svgIconSpriteUrl } from '../../utils/globals';
import { render, svg } from 'lit-html';
import { scrollToElement } from '../../utils/misc';
import { startLoadingAnimation, stopLoadingAnimation } from '../../utils/dom/loading';
import { ErrorCode } from './errorUtils';
import { ErrorCode as LtaApiErrorCode, placeOrder } from './place-order-api';
import { ClassNames } from '../../utils/dom/css-classes';
import { PLAYERS_SELECT_SELECTOR } from '../CheckoutAddressDetails';
import Request, { ApiMode } from '../../utils/request';
import dataLayer from '../../utils/data-layer';

const PAYMENT_METHOD_EVENT = 'paymentmethod';
const SMALL_VIEWPORT_HEADING_SELECTOR = '.u-title1';
const LARGE_VIEWPORT_HEADING_SELECTOR = '.u-herotitle2';
const IS_LOCAL = false;

const PaymentErrorCode = {
	...ErrorCode,
	...LtaApiErrorCode
};

const PaymentInputIds = Object.freeze({
	Wallet: 'wallet-input',
	Card: 'card-input'
});

const WalletLabels = Object.freeze({
	Google: 'Google Pay',
	Apple: 'Apple Pay'
});

export const insertErrorContainer = (headingSelector, isLargeViewport) => {
	const viewportHeading = SelectorEngine.findOne(headingSelector);
	const errorContainer = document.createElement('div');
	errorContainer.className = [
		'p-checkout__submission_error_wrapper',
		isLargeViewport ? 'u-mb-5' : 'u-mb-3',
		isLargeViewport ? 'u-hidden xl:u-block' : 'xl:u-hidden'
	].join(' ');
	viewportHeading.after(errorContainer);
	return errorContainer;
};

export const iconMarkup = ({ iconId, width = '1em', height = '1em', className = '' }) => svg`<svg
	class="lta-icon ${className}"
	width="${width}"
	height="${height}"
	fill="currentColor"
	alt="${iconId}"
	aria-hidden="true"
>
	<use href="${svgIconSpriteUrl}#${iconId}"></use>
</svg>`;

// TODO: Customise messaging based on the error & errorCode
export const renderSubmissionErrors = (error, errorCode) => {
	const { message, errorCtas } = error;
	// Update any existing snackbars or insert them into the correct position(s).
	const snackbars = SelectorEngine.find('.p-checkout__submission_error_wrapper');
	if (snackbars.length === 0) {
		// prettier-ignore
		snackbars.push(
			insertErrorContainer(LARGE_VIEWPORT_HEADING_SELECTOR, true),
			insertErrorContainer(SMALL_VIEWPORT_HEADING_SELECTOR, false)
		);
	}
	for (const snackbar of snackbars) {
		const listItems = errorCtas
			? errorCtas
				.map(error => `<li><a href="${error.link}">${error.text}</a></li>`)
				.join('')
			: '';
		snackbar.innerHTML = `
			<div class="lta-snackbar lta-snackbar--alert has-icon">
				<p class="lta-snackbar__label title">
					${message || 'There was a problem processing your booking.'}
				</p>
				${listItems ? `<ul>${listItems}</ul>` : ''}
				<div class="lta-snackbar__icon"></div>
			</div>
		`;
		render(
			iconMarkup({ iconId: 'alert', className: 'js-lta-text-input--btn-icon' }),
			SelectorEngine.findOne('.lta-snackbar__icon', snackbar)
		);
	}

};

export const paymentErrorHandler = (error, errorCode) => {
	// Scroll to the top of the main content or the first invalid field
	const scrollToElementSelector = error.isSubmissionError ? 'main' : '.lta-text-input__wrapper.is-invalid';

	if (error.isSubmissionError) {
		renderSubmissionErrors(error, errorCode);
	}

	requestAnimationFrame(() => {
		scrollToElement(scrollToElementSelector);
	});

	// TODO - Enable Place Order button
	const placeOrderButton = SelectorEngine.findOne('#payment-btn');
	if(placeOrderButton) {
		placeOrderButton.disabled = false;
	}
	window._paymentInProgress = false;
	stopLoadingAnimation();
	
	const accordionTrigger = document.querySelector('#paymentDetailsAcc'); // button id in PaymentMethodSelector
	const accordionPanel = document.querySelector('#paymentDetails');      // panel id in PaymentMethodSelector
	
	if (accordionTrigger && accordionPanel) {
		accordionTrigger.setAttribute('aria-expanded', 'true');
		accordionPanel.hidden = false;
	}
};

const saveCardDetails = async (cardToken, stripeCustomerReference) => {
	startLoadingAnimation();
	const dataObject = {
		cardToken: cardToken,
		stripeCustomerRef: stripeCustomerReference
	};
	try {
		const url = `/api/payments/card`;
		const response = await Request.post(url, dataObject, {
			local: IS_LOCAL,
			req: {
				apiMode: ApiMode.LtaError
			}
		});
		const { Success, error } = response;
		if (Success) {
			// console.log("Success : ", Success);
		}

		if (!!error?.hasError) {
			throw error;
		}
	} catch (error) {
		if (error.hasError) {
			console.log('Error :', error);
		}
	} finally {
		stopLoadingAnimation();
	}
};

const clearCheckoutSession = () => {
	sessionStorage.removeItem('bookingToken');
	sessionStorage.removeItem('paymentidentifier');
	sessionStorage.removeItem('IminplayerCount');
	sessionStorage.removeItem('source');
	sessionStorage.removeItem('iminPlayerDetails');
	window._paymentInProgress = false;
};

export const paymentSuccessHandler = async (paymentConfirmation) => {
	const playerCount = SelectorEngine.findOne(PLAYERS_SELECT_SELECTOR)?.value;
	let bookingToken = '';
	const checkoutErrorHandler = paymentConfirmation?.checkoutErrorHandler;
	bookingToken =
		paymentConfirmation.provider === 'imin'
			? sessionStorage.getItem('bookingToken')
			: window.lta?.[paymentConfirmation?.provider]?.basket?.bookingToken;

	const makePlaceOrderRequest = placeOrder(
		bookingToken,
		paymentErrorHandler,
		playerCount,
		paymentConfirmation.extraParam,
		checkoutErrorHandler
	);

	let order;
	try {
		order = await makePlaceOrderRequest(paymentConfirmation);
	} catch(error) {
		if(error?.isSubmissionError) {
			checkoutErrorHandler(error);
		}
	}

	if(order?.Status === "REQ_AUTHORIZATION") {
		return order;
	}

	if (paymentConfirmation?.extraParam?.isSaveCard && paymentConfirmation?.extraParam?.isCardPayment) {
		await saveCardDetails(paymentConfirmation?.id, paymentConfirmation?.extraParam?.stripeCustomerRef);
	}

	if (order?.redirectUrl) {
		if(window?.lta?.checkoutDataLayerMessage) {
			dataLayer.push({
				event: window?.lta?.checkoutDataLayerMessage
			});
		}
		if(order?.typeOfBooking) {
			try {
				dataLayer.push({
					event: order.typeOfBooking,
					stepCompleted: order.typeOfBooking + 'Successful'
				});
			} catch (error) {
				console.log(error.message);
			}
		}
		clearCheckoutSession();
		window.location = order.redirectUrl;
		return order;
	} else {
		const message = order?.success ? 'Unable to confirm order placement' : order?.message;
		paymentErrorHandler(
			{ message, isSubmissionError: true, errorCtas: order?.errorCtas || [] },
			PaymentErrorCode.PlaceOrderApiError
		);
		clearCheckoutSession();
		stopLoadingAnimation();
		return order;
	}
};

export const handleWalletPay = ({ paymentGateway, paymentRequest, extraParam }) => {
	paymentRequest.on(PAYMENT_METHOD_EVENT, async (event) => {
		const paymentMethodId = event.paymentMethod.id;
		startLoadingAnimation();

		// prettier-ignore
		await takeWalletPayment(
					paymentGateway,
					paymentMethodId,
					paymentSuccessHandler,
					paymentErrorHandler,
					extraParam
				);
	});
};

export const getPaymentRequest = ({ paymentGateway, provider }) => {
	const { description, totalCost } = window.lta[provider].basket;
	const totalCostInPounds = Math.round(totalCost * 100);
	const paymentRequest = paymentGateway.paymentRequest({
		country: 'GB',
		currency: 'gbp',
		total: {
			label: description ?? `${totalCostInPounds}`,
			amount: totalCostInPounds // https://stripe.com/docs/js/appendix/payment_item_object#payment_item_object-amount
		},
		requestPayerName: true,
		requestPayerEmail: true
	});
	return paymentRequest;
};

export const setupRadioInputs = ({
	cardFieldsetWrapper,
	radioInputs,
	paymentGateway,
	paymentRequest,
	placeOrderButton,
	walletSubmitButton,
	getExtraParam
}) => {
	for (const radioInput of radioInputs) {
		EventHandler.on(radioInput, 'change', () => {
			radioInput.setAttribute('checked', true);

			if (radioInput.id === PaymentInputIds.Wallet) {
				const extraParam = getExtraParam();
				cardFieldsetWrapper.classList.add(ClassNames.Hidden);
				placeOrderButton.classList.add(ClassNames.Hidden);
				walletSubmitButton.classList.remove(ClassNames.Hidden);
				handleWalletPay({ paymentGateway, paymentRequest, extraParam});
			}

			if (radioInput.id === PaymentInputIds.Card) {
				cardFieldsetWrapper.classList.remove(ClassNames.Hidden);
				placeOrderButton.classList.remove(ClassNames.Hidden);
				walletSubmitButton.classList.add(ClassNames.Hidden);
				paymentRequest.off(PAYMENT_METHOD_EVENT);
			}

			for (const input of radioInputs) {
				if (input !== radioInput) {
					input.setAttribute('checked', false);
				}
			}
		});
	}
};

export const setWalletLabel = (element, text, iconId) => {
	const NODE_TYPE_TEXT = 3;

	element.label = text;
	element.value = text;

	// Modify the text without affecting any child nodes.
	const textNode = [...element.childNodes].find(({ nodeType }) => nodeType === NODE_TYPE_TEXT);
	if (textNode) {
		textNode.nodeValue = text;
	}

	// TODO: Refactor to allow passing of children via a slot, rather than just text.
	const iconContainer = SelectorEngine.findOne('.lta-form-check__label-instruction', element);
	if (iconContainer) {
		iconContainer.classList.remove('lta-form-check__label-instruction');
		render(iconMarkup({ iconId, width: '4em', height: '3em', className: 'p-checkout__payment-icon' }), iconContainer);
	}
};

export const setupWallet = ({ paymentGateway, paymentRequest, totalCost, walletSubmitButton, walletSubmitButtonSelector, provider }) => {
	const walletForm = SelectorEngine.findOne('#wallet-form');
	const walletLabel = SelectorEngine.findOne('.lta-form-check-label', walletForm);

	// eslint-disable-next-line promise/catch-or-return
	paymentRequest.canMakePayment().then((result) => {
		// eslint-disable-next-line promise/always-return
		if (result) {
			const { applePay, googlePay } = result;

			const isValidPayment = totalCost !== 0 && (applePay || googlePay);
			const isAppleEnabled = window?.lta[provider]?.checkout?.applePayEnabled;

			// Show form and button only when item is not free and google or apple is available.
			if (isValidPayment) {
				if (applePay && !isAppleEnabled) {
					return;
				}

				const prButton = paymentGateway.elements.create('paymentRequestButton', {
					paymentRequest: paymentRequest
				});
				prButton.mount(walletSubmitButtonSelector);

				walletForm.classList.remove(ClassNames.Hidden);
				walletSubmitButton.classList.add(ClassNames.Hidden);
			}

			if (applePay && isAppleEnabled) {
				setWalletLabel(walletLabel, WalletLabels.Apple, 'apple-pay');
			}
			if (googlePay) {
				setWalletLabel(walletLabel, WalletLabels.Google, 'google-pay');
			}
		}
	});
};
